/**
 * "Which assets?" in Send for review — choosing the schema the "By asset data"
 * scope opens on, and the field it preselects.
 *
 * Pure data helpers, kept out of `ReviewInternalStep` so they can be tested
 * without the component: importing that file pulls in the `src/components`
 * barrel and, through it, an untransformed ESM dependency that jest cannot
 * parse. Same reason `reviewRows.ts` sits beside it.
 */
import { AssetSchema, AssetSchemaProperty } from 'src/types/TSchemes';

/** One row of the data filter: a schema property and the value to match. */
export type FieldRow = { field: string; value: string };

/** The name the backend's `general` preset is created under, and the only way
 *  to recognise it: a schema row carries no preset id, so
 *  `_find_or_create_general_schema` in `filespin/core/assetschema.py` matches
 *  on this exact name too. Keep the two in step. */
const GENERAL_SCHEMA_NAME = 'General Asset Schema';

/** Schema id 0 is the built-in "Default Asset Schema" — one internal
 *  search property and nothing to filter on. It comes back in the ACTIVE list
 *  and is excluded everywhere it would be offered as a choice (see
 *  `DefaultUploadSchemaSetting`, which drops it for the same reason). */
export const isSelectableSchema = (schema: AssetSchema): boolean => schema.id !== 0;

export const isGeneralSchema = (schema: AssetSchema): boolean =>
  (schema.name?.en || '').trim() === GENERAL_SCHEMA_NAME;

/**
 * Which schema "By asset data" should open on: the first real custom schema,
 * else General, else nothing — in which case the caller hides the option
 * entirely rather than offering a filter with no schema behind it.
 *
 * General is the *fallback*, not the preference: it is auto-provisioned for
 * accounts that have no schemas of their own, so if a custom schema exists it
 * is the one describing this customer's assets.
 */
export function pickDefaultSchema(schemas: AssetSchema[]): AssetSchema | undefined {
  const selectable = schemas.filter(isSelectableSchema);
  return selectable.find((s) => !isGeneralSchema(s)) ?? selectable.find(isGeneralSchema);
}

/** The `status` property, if this schema has one. Matched case-insensitively
 *  on the key: every preset spells it `status`, but a hand-built schema may
 *  not, and getting this wrong just means no field is preselected. */
export function findStatusKey(properties: Record<string, AssetSchemaProperty>): string | undefined {
  return Object.keys(properties).find((key) => key.toLowerCase() === 'status');
}

/**
 * The filter rows a freshly picked schema starts with. "By asset data (such
 * as status)" promises status, so seed that row and leave the value empty —
 * the value is the one thing only the requester can supply. A schema with no
 * status field still gets an empty row, so the shape of the answer ("pick a
 * field, pick a value") is the same either way.
 */
export function seedRowsFor(schema: AssetSchema | undefined): FieldRow[] {
  const properties = schema?.schema?.properties ?? {};
  return [{ field: findStatusKey(properties) ?? '', value: '' }];
}
